
// Deployment trigger: 2026-04-24
import React, { useState, useMemo, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Gamepad2, 
  Users, 
  Settings, 
  Plus, 
  PieChart,
  Menu,
  Briefcase,
  Globe,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  LogOut,
  Loader2,
  User as UserIcon,
  ChevronRight,
  Mail,
  Smartphone,
  Search,
  LayoutGrid,
  Shuffle,
  AlertCircle,
  Pencil,
  X
} from 'lucide-react';
import { INITIAL_PRIZES, INITIAL_RAFFLE_PRIZES, DEFAULT_ENABLED_GAMES, DEFAULT_QUESTIONS } from './constants';
import { Campaign, GameType, Project, Prize, GlobalConfig } from './types';
import Dashboard from './components/Dashboard';
import CampaignList from './components/CampaignList';
import CampaignEditor from './components/CampaignEditor';
import LeadsTable from './components/LeadsTable';
import ProjectManager from './components/ProjectManager';
import GlobalSettings from './components/GlobalSettings';
import Auth from './components/Auth';
import DrawWinnersDashboard from './components/DrawWinnersDashboard';
import PublicCampaign from './components/PublicCampaign';
import CampaignFinder from './components/CampaignFinder';
import EmbedCode from './components/EmbedCode';
import { UsageHistoryModal } from './components/UsageHistoryModal';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  collection, 
  query, 
  where, 
  onSnapshot,
  deleteDoc,
  addDoc,
  or
} from 'firebase/firestore';

type Page = 'dashboard' | 'campaigns' | 'leads' | 'settings' | 'editor' | 'projects' | 'global-settings' | 'draw-winners' | 'campaign-finder';

const App: React.FC = () => {
  const [publicCampaignId, setPublicCampaignId] = useState<string | null>(null);

  useEffect(() => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    
    if (path.startsWith('/campaign/')) {
      const id = path.split('/campaign/')[1];
      if (id) setPublicCampaignId(id);
    } else if (searchParams.has('campaign')) {
      const id = searchParams.get('campaign');
      if (id) setPublicCampaignId(id);
    } else if (hash.startsWith('#/campaign/')) {
      const id = hash.split('#/campaign/')[1];
      if (id) setPublicCampaignId(id);
    }
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string>('user');
  const [authLoading, setAuthLoading] = useState(true);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    const saved = localStorage.getItem('myeplay_current_page');
    return (saved as Page) || 'projects';
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUsageHistoryOpen, setIsUsageHistoryOpen] = useState(false);
  
  // Real Data State
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(() => {
    return localStorage.getItem('myeplay_current_project_id');
  });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [drawingCampaign, setDrawingCampaign] = useState<Campaign | null>(null);

  useEffect(() => {
    let pageToSave = currentPage;
    if (currentPage === 'editor' || currentPage === 'draw-winners') {
      pageToSave = 'campaigns';
    }
    localStorage.setItem('myeplay_current_page', pageToSave);
  }, [currentPage]);

  useEffect(() => {
    if (currentProjectId) {
      localStorage.setItem('myeplay_current_project_id', currentProjectId);
      if (currentPage === 'projects') {
        setCurrentPage('dashboard');
      }
    } else {
      localStorage.removeItem('myeplay_current_project_id');
      if (currentPage !== 'projects' && currentPage !== 'global-settings') {
        setCurrentPage('projects');
      }
    }
  }, [currentProjectId, currentPage]);
  
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>({
    platformName: 'Business Name',
    supportEmail: 'support@business.com',
    defaultPrizes: [...INITIAL_PRIZES],
    defaultRafflePrizes: [...INITIAL_RAFFLE_PRIZES],
    enableAnalytics: true,
    enabledGames: [...DEFAULT_ENABLED_GAMES]
  });

  useEffect(() => {
    document.title = globalConfig.platformName || 'Business Name';
  }, [globalConfig.platformName]);

  const handleUpdateGlobalConfig = async (newConfig: GlobalConfig) => {
    setGlobalConfig(newConfig);
    if (user) {
      try {
        // Strip undefined values which cause Firestore updateDoc to fail silently or throw
        const cleanConfig = JSON.parse(JSON.stringify(newConfig));
        await updateDoc(doc(db, 'users', user.uid), {
          globalConfig: cleanConfig
        });
      } catch (err) {
        console.error("Failed to save global config:", err);
        throw err;
      }
    }
  };

  // Auth & Provisioning
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setIsProvisioning(true);
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          const defaultUsername = currentUser.displayName || currentUser.email?.split('@')[0] || `user_${currentUser.uid.slice(0, 5)}`;

          if (!userDoc.exists()) {
            // New users are always provisioned as 'user'. Admin access is granted
            // by an existing admin (role-based, see Firestore rules). The very
            // first admin is seeded once directly in the Firebase Console.
            await setDoc(userDocRef, {
              username: defaultUsername,
              email: currentUser.email,
              uid: currentUser.uid,
              role: 'user',
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              provisionedAt: serverTimestamp(),
              globalConfig: globalConfig
            });
            setUserRole('user');
            await setDoc(doc(db, 'usernames', defaultUsername.toLowerCase()), {
              email: currentUser.email,
              uid: currentUser.uid
            });
          } else {
            const data = userDoc.data();
            setUserRole(data.role || 'user');

            if (data.globalConfig) {
              setGlobalConfig(prev => ({
                ...prev,
                ...data.globalConfig
              }));
            }
            await updateDoc(userDocRef, {
              lastLogin: serverTimestamp(),
              email: currentUser.email
            });
          }
        } catch (err) {
          console.error("Provisioning error:", err);
        } finally {
          setIsProvisioning(false);
        }
      } else {
        setUserRole('user');
      }
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Real-time Projects Listener
  useEffect(() => {
    if (!user) {
      setProjects([]);
      return;
    }

    const q = query(
      collection(db, 'projects'), 
      or(
        where('ownerId', '==', user.uid),
        where('allowedEmails', 'array-contains', user.email)
      )
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Project[];
      setProjects(projectsData);
    });

    return () => unsubscribe();
  }, [user]);

  // Real-time Campaigns Listener
  useEffect(() => {
    if (!currentProjectId) {
      setCampaigns([]);
      return;
    }

    setDataLoading(true);
    const q = query(collection(db, 'campaigns'), where('projectId', '==', currentProjectId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const campaignsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Campaign[];
      setCampaigns(campaignsData);
      setDataLoading(false);
    });

    return () => unsubscribe();
  }, [currentProjectId]);

  const activeProject = useMemo(() => 
    projects.find(p => p.id === currentProjectId) || null
  , [projects, currentProjectId]);

  const stats = useMemo(() => {
    const totalLeads = campaigns.reduce((acc, c) => acc + (c.leadsCount || 0), 0);
    const totalViews = campaigns.reduce((acc, c) => acc + (c.viewCount || 0), 0);
    return {
      totalLeads,
      totalViews,
      activeCampaigns: campaigns.filter(c => {
        if (!c.active) return false;
        if (!c.endDate) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const [year, month, day] = c.endDate.split('-').map(Number);
        const end = new Date(year, month - 1, day);
        return today <= end;
      }).length,
      avgConversion: totalViews > 0 ? (totalLeads / totalViews) * 100 : 0
    };
  }, [campaigns]);

  const handleSignOut = async () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      try {
        await signOut(auth);
        localStorage.removeItem('myeplay_current_project_id');
        localStorage.removeItem('myeplay_current_page');
        setCurrentProjectId(null);
        setCurrentPage('projects');
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setCurrentPage('editor');
    setIsSidebarOpen(false);
  };

  const handleDrawWinners = (campaign: Campaign) => {
    setDrawingCampaign(campaign);
    setCurrentPage('draw-winners');
    setIsSidebarOpen(false);
  };

  const handleCreateCampaign = () => {
    if (!currentProjectId) return;

    // Default a new campaign to an enabled game (prefer Spin To Win).
    const enabled = globalConfig.enabledGames ?? DEFAULT_ENABLED_GAMES;
    const defaultType = enabled.includes(GameType.SPIN_WHEEL) ? GameType.SPIN_WHEEL : enabled[0];
    const isRaffleDefault = defaultType === GameType.RAFFLE;

    const newCampaign: Partial<Campaign> = {
      projectId: currentProjectId,
      name: 'New Game Campaign',
      type: defaultType,
      prizes: [...(isRaffleDefault ? globalConfig.defaultRafflePrizes : globalConfig.defaultPrizes)],
      questions: defaultType === GameType.TRIVIA ? [...DEFAULT_QUESTIONS] : undefined,
      active: true,
      createdAt: new Date().toISOString().split('T')[0],
      leadsCount: 0,
      viewCount: 0,
      conversionRate: 0
    };
    setEditingCampaign(newCampaign as Campaign);
    setCurrentPage('editor');
    setIsSidebarOpen(false);
  };

  const handleSaveCampaign = async (campaign: Campaign) => {
    try {
      if (campaign.id) {
        const { id, ...data } = campaign;
        await updateDoc(doc(db, 'campaigns', id), { ...data, businessName: activeProject?.name || globalConfig.platformName, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, 'campaigns'), {
          ...campaign,
          ownerId: user?.uid,
          businessName: activeProject?.name || globalConfig.platformName,
          createdAt: new Date().toISOString().split('T')[0],
          serverCreatedAt: serverTimestamp()
        });
      }
      setCurrentPage('campaigns');
    } catch (err) {
      console.error("Save campaign error:", err);
      alert("Failed to save campaign. Check database rules.");
    }
  };

  const handleAutoSaveCampaign = async (campaign: Campaign) => {
    try {
      if (campaign.id) {
        const { id, ...data } = campaign;
        await updateDoc(doc(db, 'campaigns', id), { ...data, businessName: activeProject?.name || globalConfig.platformName, updatedAt: serverTimestamp() });
      } else {
        const docRef = await addDoc(collection(db, 'campaigns'), {
          ...campaign,
          ownerId: user?.uid,
          businessName: activeProject?.name || globalConfig.platformName,
          createdAt: new Date().toISOString().split('T')[0],
          serverCreatedAt: serverTimestamp()
        });
        setEditingCampaign({ ...campaign, id: docRef.id });
      }
    } catch (err) {
      console.error("Auto-save campaign error:", err);
    }
  };

  const toggleCampaignStatus = async (id: string) => {
    const camp = campaigns.find(c => c.id === id);
    if (camp) {
      await updateDoc(doc(db, 'campaigns', id), { active: !camp.active });
    }
  };

  const deleteCampaign = async (id: string) => {
    if (confirm("Delete this campaign forever?")) {
      await deleteDoc(doc(db, 'campaigns', id));
    }
  };

  const updateCampaignEndDate = async (id: string, endDate: string) => {
    await updateDoc(doc(db, 'campaigns', id), { endDate, updatedAt: serverTimestamp() });
  };

  const handleProjectSelect = (pId: string) => {
    setCurrentProjectId(pId);
    setCurrentPage('dashboard');
  };

  const handleSwitchProject = () => {
    setCurrentProjectId(null);
    setCurrentPage('projects');
    setIsSidebarOpen(false);
  };

  const handleUpdateActiveProject = async (updatedProject: Project) => {
    const { id, ...data } = updatedProject;
    await updateDoc(doc(db, 'projects', id), data);
  };

  // Admin-only inline quota editing from the sidebar usage panel.
  const isAdmin = userRole === 'admin';
  const [editingQuota, setEditingQuota] = useState(false);
  const [quotaDraft, setQuotaDraft] = useState({ email: 0, sms: 0 });

  const openQuotaEditor = () => {
    if (!activeProject) return;
    setQuotaDraft({ email: activeProject.emailQuota || 0, sms: activeProject.smsQuota || 0 });
    setEditingQuota(true);
  };

  const saveQuota = async () => {
    if (!activeProject) return;
    await handleUpdateActiveProject({
      ...activeProject,
      emailQuota: Math.max(0, quotaDraft.email || 0),
      smsQuota: Math.max(0, quotaDraft.sms || 0),
    });
    setEditingQuota(false);
  };

  if (publicCampaignId) {
    return <PublicCampaign campaignId={publicCampaignId} />;
  }

  if (authLoading || isProvisioning) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
          <div className="text-center">
             <span className="text-white font-black uppercase tracking-[0.3em] text-[10px] block mb-1">
               {isProvisioning ? 'Syncing Profile' : 'Authenticating'}
             </span>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return <Auth />;

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard /> },
    { id: 'campaigns', label: 'Games', icon: <PieChart /> },
    { id: 'leads', label: 'Leads', icon: <Users /> },
    { id: 'campaign-finder', label: 'Campaign Finder', icon: <Search /> },
    { id: 'settings', label: 'Settings', icon: <Settings /> },
  ];

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden text-slate-900">
      <aside className={`fixed inset-y-0 left-0 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 bg-slate-900 text-white transition-all duration-300 flex flex-col w-64 z-50 border-r border-slate-800`}>
        <div className="p-6">
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight mb-8">
            <div className="bg-indigo-500 p-1.5 rounded-lg">
              <Gamepad2 className="w-6 h-6" />
            </div>
            <span>{globalConfig.platformName}</span>
          </div>

          {activeProject && (
            <div className="relative mb-6">
              <label className="text-[10px] uppercase font-bold text-slate-500 mb-2 block tracking-widest px-2">Workspace</label>
              <button onClick={handleSwitchProject} className="w-full bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 text-left hover:bg-slate-800 transition-colors group">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50/20 flex items-center justify-center text-indigo-400 shrink-0">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div className="truncate flex-1">
                    <div className="text-xs font-bold truncate leading-tight">{activeProject.name}</div>
                    <div className="text-[10px] text-slate-400 truncate tracking-tight">{activeProject.domain}</div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>
          )}
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto scrollbar-hide">
          {activeProject ? (
            <>
              {navItems.map((item) => (
                <button 
                  key={item.id}
                  onClick={() => { setCurrentPage(item.id as Page); setIsSidebarOpen(false); }}
                  className={`flex items-center gap-3 w-full p-3 rounded-xl transition-all ${
                    currentPage === item.id || (item.id === 'campaigns' && currentPage === 'editor')
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' 
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <span className="w-5 h-5">{item.icon}</span>
                  <span className="font-medium text-sm">{item.label}</span>
                </button>
              ))}
              
              <div className="mt-6 px-2">
                {isAdmin && editingQuota ? (
                  <div className="p-4 bg-slate-800/60 rounded-2xl border border-slate-700/50 space-y-3">
                     <div className="flex items-center justify-between">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-300">Edit Quotas</span>
                        <button onClick={() => setEditingQuota(false)} className="text-slate-400 hover:text-white transition-colors"><X className="w-3.5 h-3.5" /></button>
                     </div>
                     <div>
                        <label className="text-[8px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Email Quota</label>
                        <input
                          type="number" min={0} value={quotaDraft.email}
                          onChange={e => setQuotaDraft(d => ({ ...d, email: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs font-bold outline-none focus:border-indigo-500"
                        />
                     </div>
                     <div>
                        <label className="text-[8px] font-black uppercase tracking-widest text-slate-500 mb-1 block">SMS Quota</label>
                        <input
                          type="number" min={0} value={quotaDraft.sms}
                          onChange={e => setQuotaDraft(d => ({ ...d, sms: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-xs font-bold outline-none focus:border-indigo-500"
                        />
                     </div>
                     <button onClick={saveQuota} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors">Save Quotas</button>
                  </div>
                ) : (
                  <div className="relative group">
                    <button
                      onClick={() => setIsUsageHistoryOpen(true)}
                      className="w-full text-left p-4 bg-slate-800/40 hover:bg-slate-800/60 transition-colors rounded-2xl border border-slate-700/50 space-y-4 cursor-pointer"
                    >
                       <div className="space-y-1">
                          <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-slate-400 group-hover:text-slate-300 transition-colors">
                             <span>Email Usage</span>
                             <span>{activeProject.emailUsed || 0} / {activeProject.emailQuota}</span>
                          </div>
                          <div className="w-full bg-slate-700 h-1 rounded-full overflow-hidden">
                             <div
                               className={`h-full transition-all duration-500 ${ ((activeProject.emailUsed || 0) / activeProject.emailQuota) > 0.9 ? 'bg-red-500' : 'bg-indigo-500' }`}
                               style={{ width: `${Math.min(100, ((activeProject.emailUsed || 0) / activeProject.emailQuota) * 100)}%` }}
                             />
                          </div>
                       </div>
                       <div className="space-y-1">
                          <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-slate-400 group-hover:text-slate-300 transition-colors">
                             <span>SMS Usage</span>
                             <span>{activeProject.smsUsed || 0} / {activeProject.smsQuota}</span>
                          </div>
                          <div className="w-full bg-slate-700 h-1 rounded-full overflow-hidden">
                             <div
                               className={`h-full transition-all duration-500 ${ ((activeProject.smsUsed || 0) / activeProject.smsQuota) > 0.9 ? 'bg-red-500' : 'bg-emerald-500' }`}
                               style={{ width: `${Math.min(100, ((activeProject.smsUsed || 0) / activeProject.smsQuota) * 100)}%` }}
                             />
                          </div>
                       </div>
                    </button>
                    {isAdmin && (
                      <button
                        onClick={openQuotaEditor}
                        title="Edit quotas"
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-slate-900/70 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="px-2 py-4">
               <button 
                onClick={() => setCurrentPage('projects')}
                className={`flex items-center gap-3 w-full p-3 rounded-xl transition-all ${currentPage === 'projects' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
               >
                 <Briefcase className="w-5 h-5" />
                 <span className="text-sm font-medium">Manage Projects</span>
               </button>
            </div>
          )}
          
          {(projects.length === 0 || projects.some(p => p.ownerId === user.uid)) && (
            <div className="pt-4 border-t border-slate-800 mt-4">
              <button onClick={() => { setCurrentPage('global-settings'); setIsSidebarOpen(false); }} className={`flex items-center gap-3 w-full p-3 rounded-xl transition-all text-sm font-medium ${currentPage === 'global-settings' ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-600/30' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                 <ShieldCheck className="w-5 h-5" />
                 <span>Settings</span>
              </button>
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-slate-800">
           <div className="bg-slate-800/30 rounded-2xl p-4 flex flex-col gap-4 border border-slate-700/30">
              <div className="flex items-center gap-3">
                 <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                    <UserIcon className="w-4 h-4" />
                 </div>
                 <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold truncate leading-none mb-1 text-white">{user.email?.split('@')[0]}</p>
                    <p className="text-[9px] text-slate-500 truncate">{user.email}</p>
                 </div>
              </div>
              <button onClick={handleSignOut} className="flex items-center justify-center gap-2 w-full p-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest">
                <LogOut className="w-3 h-3" />
                Sign Out
              </button>
           </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto w-full relative">
        <header className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-200 lg:hidden px-4 py-3 flex items-center justify-between z-40">
          <button onClick={() => setIsSidebarOpen(true)} className="p-2 text-slate-600"><Menu className="w-6 h-6" /></button>
          <div className="flex items-center gap-2">
             <Gamepad2 className="w-5 h-5 text-indigo-600" />
             <span className="font-black text-slate-800 text-sm tracking-tight">{globalConfig.platformName}</span>
          </div>
          <button onClick={handleSignOut} className="p-2 text-red-500"><LogOut className="w-5 h-5" /></button>
        </header>

        <div className={`p-4 sm:p-6 lg:p-8 ${!activeProject && currentPage !== 'global-settings' ? 'max-w-6xl mx-auto' : ''}`}>
          {activeProject && currentPage !== 'global-settings' && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
                  {currentPage === 'dashboard' && 'Analytics Overview'}
                  {currentPage === 'campaigns' && 'Manage Games'}
                  {currentPage === 'leads' && 'Customer Leads'}
                  {currentPage === 'campaign-finder' && 'Campaign Finder'}
                  {currentPage === 'editor' && (editingCampaign?.id ? 'Edit Campaign' : 'New Campaign')}
                  {currentPage === 'settings' && 'Project Settings'}
                </h1>
              </div>
              
              <div className="flex items-center gap-3 w-full sm:w-auto">
                {currentPage !== 'settings' && currentPage !== 'leads' && currentPage !== 'editor' && currentPage !== 'campaign-finder' && (
                  <button onClick={handleCreateCampaign} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg w-full sm:w-auto justify-center text-sm">
                    <Plus className="w-4 h-4" />
                    New Game
                  </button>
                )}
              </div>
            </div>
          )}

          {!activeProject && currentPage === 'projects' && (
            <div className="pt-10">
              <ProjectManager
                projects={projects}
                onSelect={handleProjectSelect}
                activeProjectId={currentProjectId || 'none'}
                isAdmin={userRole === 'admin'}
              />
            </div>
          )}

          {currentPage === 'global-settings' && <GlobalSettings config={globalConfig} onUpdate={handleUpdateGlobalConfig} userRole={userRole} />}

          {activeProject && (
            <>
              {dataLoading && currentPage !== 'editor' ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                </div>
              ) : (
                <>
                  {currentPage === 'dashboard' && <Dashboard stats={stats} campaigns={campaigns} project={activeProject} onOpenUsageHistory={() => setIsUsageHistoryOpen(true)} />}
                  {currentPage === 'campaigns' && <CampaignList campaigns={campaigns} onEdit={handleEditCampaign} onDrawWinners={handleDrawWinners} onToggle={toggleCampaignStatus} onDelete={deleteCampaign} onUpdateEndDate={updateCampaignEndDate} embedDisplayMode={activeProject.embedDisplayMode} />}
                  {currentPage === 'editor' && editingCampaign && <CampaignEditor campaign={editingCampaign} onSave={handleSaveCampaign} onAutoSave={handleAutoSaveCampaign} onCancel={() => setCurrentPage('campaigns')} domain={activeProject.domain} globalConfig={globalConfig} />}
                  {currentPage === 'draw-winners' && drawingCampaign && <DrawWinnersDashboard campaign={drawingCampaign} onBack={() => setCurrentPage('campaigns')} />}
                  {currentPage === 'leads' && <LeadsTable projectId={currentProjectId!} />}
                  {currentPage === 'campaign-finder' && <CampaignFinder projectId={currentProjectId!} />}
                  {currentPage === 'settings' && (
                    <div className="bg-white rounded-[32px] p-8 sm:p-12 border border-slate-200 shadow-sm animate-in fade-in slide-in-from-bottom-4">
                      <div className="flex items-center gap-4 mb-10">
                         <div className="p-3 bg-indigo-50 rounded-2xl text-indigo-600">
                            <Settings className="w-6 h-6" />
                         </div>
                         <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight italic">Project Settings</h2>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                         <div className="space-y-6">
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Identity & Domain</h3>
                            <div>
                              <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Project Name</label>
                              <input type="text" value={activeProject.name} onChange={(e) => handleUpdateActiveProject({...activeProject, name: e.target.value})} className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white transition-all font-bold outline-none shadow-inner" />
                            </div>
                            <div>
                              <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Primary Domain</label>
                              <input type="text" value={activeProject.domain} onChange={(e) => handleUpdateActiveProject({...activeProject, domain: e.target.value})} className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white transition-all font-bold outline-none shadow-inner" />
                            </div>
                         </div>

                         <div className="space-y-6 bg-slate-50/50 p-8 rounded-[32px] border border-slate-100">
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Usage Quotas (Monthly)</h3>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                               <div>
                                  <label className="flex items-center gap-2 text-[10px] font-black text-slate-500 mb-2 uppercase tracking-widest px-1">
                                    <Mail className="w-3 h-3 text-indigo-500" /> Email Quota
                                  </label>
                                  <input 
                                    type="number" 
                                    value={activeProject.emailQuota} 
                                    onChange={(e) => handleUpdateActiveProject({...activeProject, emailQuota: parseInt(e.target.value) || 0})} 
                                    className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-white font-black text-indigo-600 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all" 
                                  />
                               </div>
                               <div>
                                  <label className="flex items-center gap-2 text-[10px] font-black text-slate-500 mb-2 uppercase tracking-widest px-1">
                                    <Smartphone className="w-3 h-3 text-emerald-500" /> SMS Quota
                                  </label>
                                  <input 
                                    type="number" 
                                    value={activeProject.smsQuota} 
                                    onChange={(e) => handleUpdateActiveProject({...activeProject, smsQuota: parseInt(e.target.value) || 0})} 
                                    className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-white font-black text-emerald-600 outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all" 
                                  />
                               </div>
                            </div>

                             <div className="pt-4 space-y-4">
                               <div className="p-4 bg-white rounded-2xl border border-slate-100">
                                  <div className="flex justify-between items-center mb-2">
                                     <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Current Month</span>
                                     <span className="text-xs font-black text-indigo-600">{activeProject.emailUsed} used</span>
                                  </div>
                                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                     <div className="bg-indigo-500 h-full" style={{ width: `${Math.min(100, (activeProject.emailUsed / activeProject.emailQuota) * 100)}%` }} />
                                  </div>
                               </div>
                               <div className="p-4 bg-white rounded-2xl border border-slate-100">
                                  <div className="flex justify-between items-center mb-2">
                                     <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SMS Current Month</span>
                                     <span className="text-xs font-black text-emerald-600">{activeProject.smsUsed} used</span>
                                  </div>
                                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                     <div className="bg-emerald-500 h-full" style={{ width: `${Math.min(100, (activeProject.smsUsed / activeProject.smsQuota) * 100)}%` }} />
                                  </div>
                               </div>
                            </div>
                         </div>
                      </div>

                      <div className="mt-12 pt-12 border-t border-slate-200">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Embed Display</h3>
                        <p className="text-[11px] text-slate-400 font-medium mb-5 leading-relaxed">
                          How this project's website behaves when more than one game is active. Saves automatically.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {([
                            { mode: 'all' as const, icon: LayoutGrid, title: 'Show All Available Games', desc: 'Display every active game, each with its own launcher.' },
                            { mode: 'random' as const, icon: Shuffle, title: 'Randomly Pick 1', desc: 'Show one random game per visit (A/B testing).' },
                          ]).map(opt => {
                            const on = (activeProject.embedDisplayMode ?? 'all') === opt.mode;
                            const Icon = opt.icon;
                            return (
                              <button
                                key={opt.mode}
                                type="button"
                                onClick={() => handleUpdateActiveProject({ ...activeProject, embedDisplayMode: opt.mode })}
                                aria-pressed={on}
                                className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-all ${on ? 'border-indigo-500 bg-indigo-50/60 ring-4 ring-indigo-500/10' : 'border-slate-200 bg-slate-50/50 hover:border-slate-300'}`}
                              >
                                <div className={`p-2 rounded-xl shrink-0 ${on ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                                  <Icon className={`w-4 h-4 ${on ? 'text-white' : 'text-slate-500'}`} />
                                </div>
                                <div className="min-w-0">
                                  <div className={`text-xs font-black ${on ? 'text-indigo-700' : 'text-slate-700'}`}>{opt.title}</div>
                                  <div className="text-[10px] text-slate-400 font-medium">{opt.desc}</div>
                                </div>
                                {on && <CheckCircle2 className="w-4 h-4 text-indigo-600 ml-auto shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                        {(activeProject.embedDisplayMode ?? 'all') === 'all' && (
                          <div className="mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-200 flex items-start gap-2.5">
                            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-amber-700 font-medium leading-relaxed">
                              <span className="font-black">All active games will be shown.</span> Each one adds its own launcher to the site. For the cleanest visitor experience, we recommend keeping a single game active at a time — multiple simultaneous games can clutter the page and distract from conversions.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="mt-12 pt-12 border-t border-slate-200">
                        <EmbedCode projectId={activeProject.id} domain={activeProject.domain} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>
      
      {isUsageHistoryOpen && activeProject && (
        <UsageHistoryModal 
          projectId={activeProject.id} 
          onClose={() => setIsUsageHistoryOpen(false)} 
        />
      )}
    </div>
  );
};

export default App;
